import { atom } from 'jotai';
import { createMutationAtom } from 'atoms/create-resource-atom';
import type {
  ReconcileCreateResponse,
  ReconcilePreviewResponse,
  ReconcileUndoResponse,
} from 'types/api-responses';

// ---------------------------------------------------------------------------
// Reconcile mutation atoms (RECONCILE_TECH_SPEC §4) — one per endpoint on
// Documents::StructuredParties::ReconciliationsController.
//
// Preview is modeled as a mutation atom because it is a POST that returns data
// with the same loading/result/error semantics.
// ---------------------------------------------------------------------------

export const reconcilePreviewAtom = createMutationAtom<
  ReconcilePreviewResponse,
  'reconcilePreview'
>();

export const reconcileCreateAtom = createMutationAtom<
  ReconcileCreateResponse,
  'reconcileCreate'
>();

export const reconcileUndoAtom = createMutationAtom<
  ReconcileUndoResponse,
  'reconcileUndo'
>();

// ---------------------------------------------------------------------------
// Multi-level undo stack (RECONCILE_TECH_SPEC §2.7) — client-side, keyed by
// structured_party_id within the tab session. Undo is LIFO: only the top can be
// reversed (matching the backend's "undo top-of-stack down" rule), so a session
// stack is sufficient without a separate stack-listing endpoint.
// ---------------------------------------------------------------------------

export interface ReconcileStackEntry {
  reconciliationId: number;
  summary: string;
}

export const reconcileUndoStackAtom = atom<
  Record<number, ReconcileStackEntry[]>
>({});

// ---------------------------------------------------------------------------
// URL helpers — nested under the staged party (the structured_party_id scopes
// the undo stack to that party). Mirrors partyUrl in structured-parties.ts.
// ---------------------------------------------------------------------------

/**
 * Base URL of a staged party on a document.
 * @param {number} documentId - The document the party is staged on
 * @param {number} partyId - The staged party's id
 */
const partyBase = (documentId: number, partyId: number) =>
  `/documents/${documentId}/structured_parties/${partyId}`;

/**
 * Collection URL for a staged party's reconciliations.
 * @param {number} documentId - The document the party is staged on
 * @param {number} partyId - The staged party's id
 */
const reconciliationsBase = (documentId: number, partyId: number) =>
  `${partyBase(documentId, partyId)}/reconciliations`;

/**
 * URL to POST a new reconciliation for a staged party.
 * @param {number} documentId - The document the party is staged on
 * @param {number} partyId - The staged party's id
 */
export const reconcileCreateUrl = (documentId: number, partyId: number) =>
  reconciliationsBase(documentId, partyId);

/**
 * URL to fetch the reconciliation preview for a staged party.
 * @param {number} documentId - The document the party is staged on
 * @param {number} partyId - The staged party's id
 */
export const reconcilePreviewUrl = (documentId: number, partyId: number) =>
  `${partyBase(documentId, partyId)}/reconciliation_preview`;

/**
 * URL to undo one reconciliation of a staged party.
 * @param {number} documentId - The document the party is staged on
 * @param {number} partyId - The staged party's id
 * @param {number} reconciliationId - The reconciliation to undo
 */
export const reconcileUndoUrl = (
  documentId: number,
  partyId: number,
  reconciliationId: number
) => `${reconciliationsBase(documentId, partyId)}/${reconciliationId}/undo`;

// A stable per-tab working-session token. The backend scopes the undo stack to
// (imported_entity_id, session_id), so undo only reaches reconciles done in this
// session — persisted in sessionStorage so it survives reloads within the tab.
const SESSION_STORAGE_KEY = 'reconcile-session-id';

/**
 * This tab's reconcile session token, created and persisted on first use.
 * @return {string} The token, or a one-off fallback when sessionStorage is unavailable
 */
export function reconcileSessionId(): string {
  const fallback = `sess-${Date.now()}-${Math.round(Math.random() * 1e9)}`;
  try {
    const existing = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) return existing;
    const id = window.crypto?.randomUUID?.() ?? fallback;
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, id);
    return id;
  } catch {
    // sessionStorage unavailable (e.g. tests / privacy mode) — best-effort token.
    return fallback;
  }
}
